/**
 * Where the batteries stand — the part of AD-010 that was still open.
 *
 * ## What the original does, recovered
 *
 * `aa.c` does not choose emplacements; it is handed them. `s\game\snario.c`
 * (segment `0x061f`) interprets the mission's own scenario stream, and one of
 * its opcodes is an anti-aircraft battery: the loader's counting pass does
 * `aaCount++; skip(15)` and its placing pass does `read(record, 15); aa_add(record)`,
 * with `aa_init(aaCount)` in between sizing the 44-byte battery array. The
 * 15-byte record is exactly what `aa_add` copies out: **three `int32` of
 * position, an `int16` class flag (non-zero picks `AA.DAT`'s 50 000-unit heavy
 * radius over the 20 000-unit light one) and one `int8` side**.
 *
 * So flak placement in Red Baron is *authored per mission*, the same way the
 * aeroplanes and the ground targets in that stream are. There is no rule to
 * port. What there is, is the shape the shipped scenarios actually have, and
 * AD-010 §5 measured it from guest memory during a live mission:
 *
 * - **18 batteries, 9 per side**, in one balloon-busting sortie; 16 (8 a side)
 *   in a second run of the same type.
 * - **Spread over the whole map** — ±110 000 ft, i.e. a front about 67 km long
 *   — and explicitly *not* ringed around the objective.
 * - **12 of the 18 were `heavy`.**
 * - **Every one at `z = 0`**, i.e. on level ground.
 *
 * ## What this port does with that
 *
 * Nine batteries over 67 km of front is **one battery per 7.4 km**, and that
 * spacing is the whole of the balance answer: a heavy battery reaches 1 905 m,
 * so the original's own density leaves most of the line unwatched and makes
 * archie something you fly *into* rather than something that is always on. A
 * port mission's working area is a few kilometres across, so the same density
 * puts one battery a side near the crossing — which is what `frontLineFlak`
 * lays out, at the original's own 2-in-3 heavy ratio.
 *
 * Objective defences (the guns under a balloon) are a separate, *authored*
 * placement and live with the generator that authors them, exactly as they do
 * in the original's scenario stream.
 *
 * **Two shapes, because two kinds of map ship.** `frontLineFlak` is the belt
 * above and it needs a front to spread along; `homeDefenceFlak` at the bottom
 * of this file is the ring London gets instead, because London is the
 * home-defence region and has no wire (`mission-world.ts`'s
 * `REGION_HAS_FRONT_LINE`). `mission-assemble.ts` picks between them.
 *
 * The draws come from a `MissionDraw` salted per mission for flak alone, so
 * adding a battery cannot shift which aeroplane or which weather a generator
 * draws next (AD-007a's rule, applied at setup time).
 */

import { MissionDraw } from "./mission-build.ts";
import type { MissionFlak } from "./mission-spec.ts";
import type { FrontLine } from "./mission-world.ts";
import type { Vec3 } from "../flight/math3d.ts";

/** 67 km of front over the 9 batteries a side the original places on it. */
export const FLAK_SPACING_M = 67_000 / 9;

/** 12 of the 18 batteries in the live census carried the heavy radius. */
export const FLAK_HEAVY_SHARE = 12 / 18;

/**
 * Half-width of the strip of front a port mission actually works over: the
 * lateral reach of the widest generated route (`patrol`'s turn point at
 * ±2 600 m). Batteries are spread across it, so the count below is that width
 * measured in the original's own spacing.
 */
export const FLAK_BELT_HALF_WIDTH_M = 2_600;

/**
 * How far behind its own wire a battery sits. Far enough that it is not
 * standing in no-man's-land, close enough that its 1 905 m reach covers the
 * crossing — which is where a battery placed at the original's density is any
 * use at all.
 */
export const FLAK_SETBACK_M = 900;

export interface FlakBeltOptions {
  readonly front: FrontLine;
  /** Distinct per mission so two catalog entries on one seed do not lay identical belts. */
  readonly salt: string;
  readonly seed: number;
}

/** Batteries a side, at the original's spacing over the port's own working width. */
export function flakBatteriesPerSide(halfWidthM: number = FLAK_BELT_HALF_WIDTH_M): number {
  return Math.max(1, Math.min(3, Math.round((2 * halfWidthM) / FLAK_SPACING_M)));
}

/**
 * The standing belt: `flakBatteriesPerSide()` guns on each side of the wire,
 * spread across the working width and set back from it, at `z = 0` ground
 * level like every battery the original places.
 *
 * Both sides are laid because the original places both: nine German batteries
 * and nine Allied ones in the same mission. A friendly battery never shoots at
 * the player (`flakEngages` refuses same-side targets) but it does shoot at
 * whoever comes for him, which is why an intercept over your own aerodrome is
 * not fought alone.
 */
export function frontLineFlak(options: FlakBeltOptions): readonly MissionFlak[] {
  const draw = new MissionDraw(options.seed, `${options.salt}:flak`);
  const perSide = flakBatteriesPerSide();
  const batteries: MissionFlak[] = [];
  for (const side of ["hostile", "friendly"] as const) {
    // `friendlySign` is +1 on the friendly side of `zM`; hostile guns stand on
    // the other one.
    const sign = side === "friendly" ? options.front.friendlySign : -options.front.friendlySign;
    const prefix = side === "friendly" ? "flak-f" : "flak-h";
    for (let index = 0; index < perSide; index++) {
      const slot = (index + 0.5) / perSide;
      const x = (slot * 2 - 1) * FLAK_BELT_HALF_WIDTH_M + draw.between(-400, 400);
      const setback = FLAK_SETBACK_M + draw.between(0, 600);
      batteries.push({
        id: `${prefix}-${index + 1}`,
        side,
        position: { x, y: 0, z: options.front.zM + sign * setback },
        batteryClass: draw.chance(FLAK_HEAVY_SHARE) ? "heavy" : "light",
      });
    }
  }
  return batteries;
}

/**
 * How far out the guns stand from what they are defending, on a map with no
 * front. The London Air Defence Area's inner gun stations sat a few kilometres
 * out from what they covered so a raider was under fire *before* it was over
 * the target; 3 km also keeps a heavy battery's 1 905 m reach over the
 * objective itself.
 */
export const FLAK_RING_RADIUS_M = 3_000;

export interface FlakRingOptions {
  /** What the guns are there to defend — the region's own anchor, in world metres. */
  readonly centre: Vec3;
  /** Whose guns they are. A home-defence map has only one side's. */
  readonly side: "friendly" | "hostile";
  readonly salt: string;
  readonly seed: number;
}

/** Guns on a ring rather than a line: the same one-per-`FLAK_SPACING_M` density the original's front has, wrapped around the objective. */
export function flakBatteriesOnRing(radiusM: number = FLAK_RING_RADIUS_M): number {
  return Math.max(2, Math.min(6, Math.round((2 * Math.PI * radiusM) / FLAK_SPACING_M)));
}

/**
 * **The home-defence ring** — what a region with no front gets instead of a belt.
 *
 * `frontLineFlak` above spreads batteries across a *front*. London has none
 * (`mission-world.ts`'s `REGION_HAS_FRONT_LINE`), and with `DEFAULT_FRONT_LINE.zM`
 * standing in at 0 the belt degenerated into "put the guns within three
 * kilometres of wherever the player starts, on a line through him" — which is
 * how `london-sea-placements.md` §3.2 found all of London's batteries, and
 * every searchlight copied from them, out in the North Sea.
 *
 * What a home-defence map has instead is a defended *place*. The historical
 * answer is the London Air Defence Area: gun stations and searchlights ringing
 * the city, firing at whatever came for it. So the batteries go on a ring
 * around `centre`, at the original's own density — `FLAK_SPACING_M`, one
 * battery per 7.4 km of line, applied to the ring's circumference instead of
 * to 67 km of trench.
 *
 * **One side only, and it is the defender's.** The original's "nine a side in
 * every scenario" is a property of a front with an army on each side of it;
 * over Kent there is no German battery to place. Which side that is in mission
 * terms depends on who the player is flying for — a Gotha crew is shot at by
 * the same guns that protect an RFC pilot.
 */
export function homeDefenceFlak(options: FlakRingOptions): readonly MissionFlak[] {
  const draw = new MissionDraw(options.seed, `${options.salt}:flak`);
  const count = flakBatteriesOnRing();
  const prefix = options.side === "friendly" ? "flak-f" : "flak-h";
  const batteries: MissionFlak[] = [];
  for (let index = 0; index < count; index++) {
    // Evenly spaced around the ring, then jittered, so two missions on one seed
    // do not stand their guns on identical ground.
    const bearing = ((index + 0.5) / count) * Math.PI * 2 + draw.between(-0.2, 0.2);
    const radius = FLAK_RING_RADIUS_M + draw.between(-500, 500);
    batteries.push({
      id: `${prefix}-${index + 1}`,
      side: options.side,
      position: {
        x: options.centre.x + Math.cos(bearing) * radius,
        y: 0,
        z: options.centre.z + Math.sin(bearing) * radius,
      },
      batteryClass: draw.chance(FLAK_HEAVY_SHARE) ? "heavy" : "light",
    });
  }
  return batteries;
}
